import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { array, string } from 'prop-types';

import { INSTRUCTOR_TEMPLATE } from 'constants/routingPath';
import {
  COURSE_INSTRUCTOR_CLICK,
  REGULAR_CLASS_INSTRUCTOR_CLICK,
} from 'constants/gtmEvent';
import { TOPIC_HOME } from 'constants/liveEventSections';
import {
  COURSE_DETAIL,
  COURSE_LESSONPAGE,
} from 'constants/courseEventSections';
import { COURSE, COURSE_LESSON, REGULAR_CLASS } from 'constants/contentTypes';

import Button from 'components/Button';

import useStyles from 'hooks/useStyles';
import useGetOfficialSiteURL from 'hooks/useGetOfficialSiteURL';

import { push } from 'reduxModules/navigation/action';
import { sendGTMEvent } from 'reduxModules/googleTagManager/action';

import styles from './InstructorProfile.scss';

function InstructorProfile({ instructors: originalInstructors, contentType }) {
  useStyles(styles);
  const dispatch = useDispatch();
  const getOfficialSiteURL = useGetOfficialSiteURL();
  const { t } = useTranslation(['common']);
  const instructors = useMemo(
    () =>
      originalInstructors.map(({ about, ...props }) => ({
        about: about.slice(0, 3),
        ...props,
      })),
    [originalInstructors],
  );

  const eventNameMapping = {
    [REGULAR_CLASS]: REGULAR_CLASS_INSTRUCTOR_CLICK,
    [COURSE]: COURSE_INSTRUCTOR_CLICK,
    [COURSE_LESSON]: COURSE_INSTRUCTOR_CLICK,
  };

  const sectionNameMapping = {
    [REGULAR_CLASS]: TOPIC_HOME,
    [COURSE]: COURSE_DETAIL,
    [COURSE_LESSON]: COURSE_LESSONPAGE,
  };

  const onViewFullProfileClick = instructor => {
    dispatch(
      sendGTMEvent({
        eventName: eventNameMapping[contentType],
        data: {
          instructorId: instructor.id,
          instructorName: instructor.name,
          instructorHashtag: instructor.hashtagString,
          subjects: instructor.subjectString,
          tutorService: instructor.askableTutorId !== null,
          section: sectionNameMapping[contentType],
        },
      }),
    );
    dispatch(
      push(
        getOfficialSiteURL(INSTRUCTOR_TEMPLATE, {
          id: instructor.id,
        }),
      ),
    );
  };

  return (
    <div>
      {instructors.map(instructor => (
        <div key={instructor.id} className={styles.root}>
          <div onClick={() => onViewFullProfileClick(instructor)}>
            <img
              src={instructor.avatarUrl}
              alt={instructor.name}
              className={styles.avatar}
            />
          </div>
          <div className={styles.profile}>
            <div
              className={styles.name}
              onClick={() => onViewFullProfileClick(instructor)}
            >
              {instructor.name}
              {instructor.instructorHashtags.length > 0 && (
                <span className={styles.dot}>•</span>
              )}
              {instructor.instructorHashtags.map((hashtag, index) => (
                <span key={hashtag}>
                  {hashtag}
                  {index !== instructor.instructorHashtags.length - 1 && (
                    <span className={styles.dot}>•</span>
                  )}
                </span>
              ))}
            </div>
            {instructor.about.map(({ id: aboutId, content, description }) => (
              <div key={aboutId}>
                <div className={styles.content}>{content}</div>
                <div className={styles.description}>{description}</div>
              </div>
            ))}
            <Button
              link
              ghost
              type="primary"
              onClick={() => onViewFullProfileClick(instructor)}
              text={t('common.instructorProfileCta')}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

InstructorProfile.propTypes = {
  instructors: array.isRequired,
  contentType: string.isRequired,
};

export default InstructorProfile;
